import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminEmployeesPage from "./pages/AdminEmployeesPage";
import AdminTimesheetsPage from "./pages/AdminTimesheetsPage";
import AdminLeavePage from "./pages/AdminLeavePage";
import AdminOvertimePage from "./pages/AdminOvertimePage";
import AdminProjectsPage from "./pages/AdminProjectsPage";
import AdminApprovalsPage from "./pages/AdminApprovalsPage";
// New Admin Pages
import PayrollStructuresPage from "./pages/admin/PayrollStructuresPage";
import PayrollPayslipsPage from "./pages/admin/PayrollPayslipsPage";
import PayrollTaxPage from "./pages/admin/PayrollTaxPage";
import TrainingProgramsPage from "./pages/admin/TrainingProgramsPage";
import CertificationsPage from "./pages/admin/CertificationsPage";
import SkillsMatrixPage from "./pages/admin/SkillsMatrixPage";
import RecruitmentPage from "./pages/admin/RecruitmentPage";
import ContractsPage from "./pages/admin/ContractsPage";
import PerformanceReviewsPage from "./pages/admin/PerformanceReviewsPage";
import PGMOResourcesPage from "./pages/admin/PGMOResourcesPage";
import HRCompliancePage from "./pages/admin/HRCompliancePage";
import DashboardLayout from "./components/DashboardLayout";
import ExecutiveDashboard from "./pages/ExecutiveDashboard";
import PMODashboard from "./pages/PMODashboard";
import FundsDashboard from "./pages/FundsDashboard";
import TradeDashboard from "./pages/TradeDashboard";
import GovernanceDashboard from "./pages/GovernanceDashboard";
import FinancialDashboard from "./pages/FinancialDashboard";
import FunderDashboard from "./pages/FunderDashboard";
import ProjectDetail from "./pages/ProjectDetail";
import RegionsPage from "./pages/RegionsPage";
import VendorsPage from "./pages/VendorsPage";
import CRMPage from "./pages/CRMPage";
import ProjectManagement from "./pages/ProjectManagement";
import ProjectScore from "./pages/ProjectScore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `https://crm.qfinitymedia.co.za/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("cdace_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("cdace_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (userCode, password, role) => {
    const response = await axios.post(`${API}/auth/login`, {
      user_code: userCode,
      password: password,
      role: role
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("cdace_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("cdace_token");
    localStorage.removeItem("cdace_admin_user");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, setToken, setUser, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin-login" element={<AdminLoginPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/employees" element={<AdminEmployeesPage />} />
          <Route path="/admin/timesheets" element={<AdminTimesheetsPage />} />
          <Route path="/admin/leave" element={<AdminLeavePage />} />
          <Route path="/admin/overtime" element={<AdminOvertimePage />} />
          <Route path="/admin/projects" element={<AdminProjectsPage />} />
          <Route path="/admin/projects/active" element={<AdminProjectsPage />} />
          <Route path="/admin/projects/completed" element={<AdminProjectsPage />} />
          <Route path="/admin/projects/on-hold" element={<AdminProjectsPage />} />
          <Route path="/admin/projects/risk" element={<AdminProjectsPage />} />
          <Route path="/admin/approvals" element={<AdminApprovalsPage />} />
          {/* Payroll Routes */}
          <Route path="/admin/payroll/structures" element={<PayrollStructuresPage />} />
          <Route path="/admin/payroll/payslips" element={<PayrollPayslipsPage />} />
          <Route path="/admin/payroll/tax" element={<PayrollTaxPage />} />
          <Route path="/admin/payroll/benefits" element={<PayrollPayslipsPage />} />
          <Route path="/admin/payroll/pension" element={<PayrollTaxPage />} />
          {/* Training Routes */}
          <Route path="/admin/training/programs" element={<TrainingProgramsPage />} />
          <Route path="/admin/training/certifications" element={<CertificationsPage />} />
          <Route path="/admin/training/skills-matrix" element={<SkillsMatrixPage />} />
          <Route path="/admin/training/compliance" element={<TrainingProgramsPage />} />
          {/* HR Management Routes */}
          <Route path="/admin/recruitment" element={<RecruitmentPage />} />
          <Route path="/admin/contracts" element={<ContractsPage />} />
          <Route path="/admin/performance" element={<PerformanceReviewsPage />} />
          <Route path="/admin/disciplinary" element={<AdminEmployeesPage />} />
          {/* PGMO Integration Routes */}
          <Route path="/admin/pgmo/milestones" element={<PGMOResourcesPage />} />
          <Route path="/admin/pgmo/wbs" element={<PGMOResourcesPage />} />
          <Route path="/admin/pgmo/resources" element={<PGMOResourcesPage />} />
          <Route path="/admin/pgmo/budget" element={<PGMOResourcesPage />} />
          {/* CRM Integration Routes */}
          <Route path="/admin/crm/clients" element={<AdminProjectsPage />} />
          <Route path="/admin/crm/escalations" element={<AdminProjectsPage />} />
          <Route path="/admin/crm/sla" element={<AdminProjectsPage />} />
          {/* Compliance Routes */}
          <Route path="/admin/compliance/hr" element={<HRCompliancePage />} />
          <Route path="/admin/compliance/safety" element={<HRCompliancePage />} />
          <Route path="/admin/compliance/labour" element={<HRCompliancePage />} />
          <Route path="/admin/compliance/audit" element={<AdminApprovalsPage />} />
          
          {/* PGMO Routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    <Route path="/" element={<ExecutiveDashboard />} />
                    <Route path="/pmo" element={<PMODashboard />} />
                    <Route path="/project-management" element={<ProjectManagement />} />
                    <Route path="/project-score" element={<ProjectScore />} />
                    <Route path="/funds" element={<FundsDashboard />} />
                    <Route path="/trade" element={<TradeDashboard />} />
                    <Route path="/governance" element={<GovernanceDashboard />} />
                    <Route path="/financial" element={<FinancialDashboard />} />
                    <Route path="/funder" element={<FunderDashboard />} />
                    <Route path="/projects/:projectId" element={<ProjectDetail />} />
                    <Route path="/regions" element={<RegionsPage />} />
                    <Route path="/vendors" element={<VendorsPage />} />
                    <Route path="/crm" element={<CRMPage />} />
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
